import {Component, Input, OnInit} from '@angular/core';
import { PaymentAcceptStatus, PaymentService } from '../../../../core/services/impl/payment.service';
import { NotificationService } from '../../../../core/services/impl/notification.service';
import { MessageType } from '../../../models/request-offer/request-offer.enum';
import { ApplicationStateService } from '../../../services/application-state.service';
import { NotificationStateService } from '../../../services/notification-state.service';
import { StepKey } from '../../../../core/enums/step-key.enum';
import { OfferService } from '../../../../core/services/impl/offer.service';
import { NavigateManagerService } from '../../../../core/services/navigate-manager.service';
import { MetricsService } from '../../../services/metrics.service';
import { CustomMetricsName } from '../../../components/models/metrics/custom-metrics-name.enum';
import { MetricsType } from '../../../components/models/metrics/metrics-type.enum';
import { AppMode } from '../../../models/app-mode.enum';
import {AssetsService} from '../../../services/assets.service';
import {NotificationState} from '../../../models/notification-state';
import {RegistrationService} from '../../../../core/services/impl/registration.service';
import {Ga4CustomEventsHelper} from '../../../../core/utils/ga4-custom-events-helper';
import {NextNotificationService} from '../../../../core/services/impl/next-notification.service';
import {isNestBank} from '../../../../core/utils/utils';

@Component({
  selector: 'app-payment-accept-notification',
  templateUrl: './payment-accept-notification.component.html',
  styleUrls: ['./payment-accept-notification.component.scss']
})
export class PaymentAcceptNotificationComponent implements OnInit {

  @Input() notificationState: NotificationState;
  mode: OfferRecalculationMode;
  modeSpecificTranslationsContainer: string;
  private readonly translationsContainer = 'OFFER_RECALCULATION_DIALOG_COMPONENT';

  get status(): PaymentAcceptStatus {
    return this.notificationState.paymentAcceptStatus;
  }

  get isHigherPremiumMode(): boolean {
    return this.mode === OfferRecalculationMode.HIGHER_PREMIUM;
  }

  get imageUrl(): string {
    if (this.isHigherPremiumMode) {
      return this.assetsService.informationDialogImageSrc;
    } else {
      if (isNestBank()) {
        return 'assets/images/nest-bank-keys.png';
      }
      return 'assets/images/premium-discount-image.png'
    }
  }

  get paymentMethod(): any {
    return this.registrationService.registration ? this.registrationService.registration.paymentMethod : null;
  }

  constructor(private notificationService: NotificationService,
              private assetsService: AssetsService,
              private metricsService: MetricsService,
              private stateService: ApplicationStateService,
              private notificationStateService: NotificationStateService,
              private navigateManager: NavigateManagerService,
              private paymentService: PaymentService,
              private registrationService: RegistrationService,
              private offerService: OfferService,
              private nextNotificationService: NextNotificationService) {
  }

  ngOnInit() {
    this.initMode();
    this.initModeSpecificTranslationsContainer();
    this.sendComponentMetric();
    Ga4CustomEventsHelper.recalculationEvent.emit(this.paymentMethod);
  }

  backToOffers(): void {
    this.sendDecisionMetric(false);
    this.sendNotification(false);
    this.navigateManager.markStepAndPreviousStepsAsValid(StepKey.FIRST_STEP_3);
    this.navigateManager.updateCacheOffer(false);
    this.offerService.sendCalculation()
      .then(() => {
        this.stateService.mode = AppMode.RAW;
        this.offerService.noOffersEvent.take(1).subscribe(() => this.navigateManager.openNoOffersFoundDialog());
        this.navigateManager.moveToStep(StepKey.SECOND_STEP);
      })
      .catch(error => { // FIXME handle failed postCalculation
        console.error(error);
      });
  }

  pay(): void {
    this.sendDecisionMetric(true);
    this.sendNotification(true);
    this.nextNotificationService.initNextNotification(); // switch to next notification mode
    this.stateService.mode = AppMode.NOTIFICATIONS;
  }

  getTranslationKey(key: string, subContainer?: string): string {
    if (subContainer) {
      return `${this.translationsContainer}.${subContainer}.${key}`;
    }
    return `${this.translationsContainer}.${key}`;
  }

  private initMode(): void {
    this.mode = parseFloat(this.status.newAmount) > parseFloat(this.status.amount) ? OfferRecalculationMode.HIGHER_PREMIUM : OfferRecalculationMode.LOWER_PREMIUM;
  }

  private initModeSpecificTranslationsContainer(): void {
    switch (this.mode) {
      case OfferRecalculationMode.HIGHER_PREMIUM:
        this.modeSpecificTranslationsContainer = 'HIGHER_PREMIUM';
        break;
      case OfferRecalculationMode.LOWER_PREMIUM:
        this.modeSpecificTranslationsContainer = 'LOWER_PREMIUM';
        break;
    }
  }

  private sendNotification(paymentAcceptance: boolean): void {
    this.notificationService.sendNotification(MessageType.WAITING_FOR_NEW_AMOUNT_ACCEPT, {
      userDecision: paymentAcceptance
    });
  }

  private sendComponentMetric(): void {
    this.notificationStateService.sendNotificationComponentMetric('PaymentAcceptNotificationComponent', { status: this.status });
  }

  private sendDecisionMetric(paymentAcceptance: boolean): void {
    const value = paymentAcceptance ? 'pay' : 'backToOffers';
    this.metricsService.sendSimpleMetric(CustomMetricsName.PAYMENT_ACCEPTANCE, MetricsType.BUTTON, value);
  }

}

export enum OfferRecalculationMode {
  HIGHER_PREMIUM = 'HIGHER_PREMIUM',
  LOWER_PREMIUM = 'LOWER_PREMIUM'
}
